import axios from "axios";
import { navigate } from "gatsby";
import { useState, useEffect, useContext, createContext, useCallback, useReducer } from "react";
import PropTypes from "prop-types";
import { useLocation } from "@gatsbyjs/reach-router";
import { sendUserSignIn as gtmSendUserSignIn, useTracking } from "./Tracking";
import { useCurrentCountryCode } from "@hooks/usePrices";
import { useALError, alReportError } from "@helpers/ErrorBoundary/ALErrorBoundary";
import { getShopifyStoreByCountry } from "../../locale-shopifies";

import {
  isBrowser,
  getCookie,
  getAllUrlParams,
  removeQueryParam,
  safeLocalStorage,
} from "@helpers/browser";
import { getCustomerAccessTokenCreateWithMultipass } from "@helpers/general";

import CreateAccount from "@components/account/CreateAccount";
import Login from "@components/account/Login";
import LoginError from "@components/account/LoginError";
import ALPopup from "@components/al_components/ALPopup";

import REDIRECT_TYPES from "@constants/RedirectType";

const TYPE_CREATE_ACCOUNT = "signup";
const TYPE_LOGIN = "login";
const TYPE_CONFIRM = "confirm";
const TYPE_LOGIN_ERROR = "login-error";

// Popups that are exposed through the ?auth=<type> url param and can be triggered with said parameter
const URL_EXPOSED_TYPES = [TYPE_CREATE_ACCOUNT, TYPE_LOGIN];

const noop = () => {};

export const AuthContext = createContext({
  login: noop,
  logout: noop,
  accountDetails: null,
  shouldClearWishlist: false,
  showCreateAccount: noop,
  showLogin: noop,
  showConfirmation: noop,
});

function useLocalStorage(key, initialValue) {
  const localStorageReducer = (state, action) => {
    let valueToStore = state;
    try {
      valueToStore = action instanceof Function ? action(state) : action;
      safeLocalStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (err) {
      // Local Storage or func action fail
      console.error(err);
      alReportError(err);
    }
    return valueToStore;
  };
  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useReducer(localStorageReducer, initialValue, () => {
    try {
      if (isBrowser) {
        // Get from local storage by key
        const item = safeLocalStorage.getItem(key);
        // Parse stored json or if none return initialValue
        return item ? JSON.parse(item) : initialValue;
      }
      return initialValue;
    } catch (error) {
      // If error also return initialValue
      console.error(error);
      alReportError(error);
      return initialValue;
    }
  });
  return [storedValue, setStoredValue];
}

function useProvideAuth() {
  const { sendReport } = useALError();

  const countryCode = useCurrentCountryCode();
  const store = getShopifyStoreByCountry(countryCode);
  const [accountDetails, setAccountDetails] = useLocalStorage(`${store.name}-accountDetails`, null);
  const [shouldClearWishlist, setShouldClearWishlist] = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const { trackUserSignup, trackUserLogin, trackAccountErrors } = useTracking();

  const location = useLocation();

  const [authModalComponent, setAuthModalComponent] = useState(null);
  const [authModalComponentProps, setAuthModalComponentProps] = useState({});

  // When resetting authModalComponent to null (i.e. closing modal)
  // Reset component props and any lingering &auth=<type> query param that might have it triggered
  useEffect(() => {
    if (!authModalComponent) {
      // Reset component props
      setAuthModalComponentProps({});

      // Remove &auth=<type> parameter
      removeQueryParam("auth");
    }
  }, [authModalComponent]);

  // The failed link URL must not stay in the address bar, otherwise login() would use it,
  // expired token included, as the redirect of the newly requested link.
  // The original destination is kept in auth-redirect so the new link still leads there.
  const showLoginError = useCallback((redirectUrl) => {
    navigate(`/?auth-redirect=${encodeURIComponent(redirectUrl)}`, { replace: true });
    setAuthModalComponent(TYPE_LOGIN_ERROR);
  }, []);

  /*
    Listen to location changes - for the auth search parameter
    URL_EXPOSED_TYPES contains popups that can be opened through the url query parameter
    i.e. http://localhost:8888/collections/bestsellers?auth=login will open the login popup
  */
  useEffect(() => {
    const queryParams = getAllUrlParams(location.href);
    if (queryParams.auth && URL_EXPOSED_TYPES.indexOf(queryParams.auth) > -1) {
      setAuthModalComponent(queryParams.auth);
    }

    const isValidRedirect = !Object.values(REDIRECT_TYPES).some(
      (redirect) => redirect === queryParams.redirect
    );
    if (queryParams.magic && isValidRedirect) {
      const redirectUrl = queryParams.redirect || `${window.location.origin}/account/`;
      const redirect = new URL(redirectUrl, window.location.origin);
      let cleanRedirectUrl = redirectUrl;
      if (redirect.searchParams.getAll("auth").includes("login")) {
        redirect.searchParams.delete("auth", "login");
        cleanRedirectUrl = redirect.toString();
      }
      axios
        .get(`/.netlify/functions/magic`, {
          params: {
            token: queryParams.magic,
            redirectUrl: cleanRedirectUrl,
            countryCode,
          },
        })
        .then((res) => {
          const { token, authUrl, email } = res.data;
          if (!token) {
            trackAccountErrors({
              action: "LOGIN-MAGIC",
              errorMessage: "Missing token from magic link return",
              userEmail: email || "",
            });
            showLoginError(cleanRedirectUrl);
            return;
          }
          getCustomerAccessTokenCreateWithMultipass(token, store)
            .then((customerAccessToken) => {
              const { accessToken, expiresAt } = customerAccessToken;

              document.cookie = `${store.name}-accessToken=${accessToken}; expires=${new Date(
                expiresAt
              ).toGMTString()}; path=/; Secure`;

              if (!getCookie(`${store.name}-accessToken`)) {
                trackAccountErrors({
                  action: "LOGIN-MAGIC",
                  errorMessage: "Failed to set cookie",
                  userEmail: email || "",
                });
              }

              // Auth URL is a Shopify multipass URL that will redirect to the 'redirectUrl'
              window.location.href = authUrl;
            })
            .catch((err) => {
              trackAccountErrors({
                action: "LOGIN-MAGIC",
                errorMessage: `Failed to get customer access token getCustomerAccessTokenCreateWithMultipass() - ${err.message}`,
                userEmail: email || "",
              });
              sendReport(err, { name: "AuthProvider", priority: "P1", email });
              showLoginError(cleanRedirectUrl);
            });
        })
        .catch((err) => {
          trackAccountErrors({
            action: "LOGIN-MAGIC",
            errorMessage: "Failed to get multipass token from magic link",
            userEmail: "",
          });
          sendReport(err, { name: "AuthProvider", priority: "P1" });
          showLoginError(cleanRedirectUrl);
        });
    } else if (queryParams.redirect && !queryParams.magic && isValidRedirect) {
      window.location.href = queryParams.redirect;
    }
  }, [countryCode, location, sendReport, store, trackAccountErrors, showLoginError]);

  const signup = async (email, password, firstName, lastName, redirectUrl = null) => {
    try {
      setIsLoading(true);
      const res = await axios.post(
        `/.netlify/functions/account-register`,
        {
          email,
          password,
          firstName,
          lastName,
          countryCode,
          redirectUrl: redirectUrl || window.location.href,
        },
        {
          throwHttpErrors: false,
        }
      );

      try {
        window.edgetag("data", {
          email,
          firstName,
          lastName,
          country: countryCode,
        });
      } catch (err) {
        sendReport(err, { name: "JustUnoTracking", priority: "P1" });
      }

      const { authUrl, token, customer } = res.data;
      trackUserSignup(email, res.status, res.data, res.status === 200 && authUrl && token);
      if (res.status === 200 && authUrl && token) {
        setError(null);

        const customerAccessToken = await getCustomerAccessTokenCreateWithMultipass(token, store);
        const { accessToken, expiresAt } = customerAccessToken;

        document.cookie = `${store.name}-accessToken=${accessToken}; expires=${new Date(
          expiresAt
        ).toGMTString()}; path=/; Secure`;

        setAccountDetails(customer);

        window.location.href = authUrl;

        return true;
      }
      setError(`Failed to register: ${res.data}`);
      trackAccountErrors({
        action: "SIGNUP",
        errorMessage: `Failed to register: ${res.data}`,
        userEmail: email,
      });
    } catch (e) {
      const res = e.response;
      trackAccountErrors({
        action: "SIGNUP",
        errorMessage: `Failed to register: ${res.data}`,
        userEmail: email,
      });
      setError(`Failed to register: ${res ? res.data : JSON.stringify(e)}`);
      sendReport(e, { name: "AuthProvider", priority: "P5" });
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email) => {
    try {
      setIsLoading(true);
      const queryParams = getAllUrlParams(location.href);
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.delete("auth"); // do not redirect to the login popup
      const redirectUrl = queryParams["auth-redirect"] || currentUrl.toString();
      const response = await axios.post("/.netlify/functions/magic", {
        countryCode,
        redirectUrl,
        email,
      });

      if (response.status === 200) {
        gtmSendUserSignIn(email);
        trackUserLogin(email);
        return true;
      }
      trackAccountErrors({
        action: "LOGIN",
        errorMessage: `HTTP status: ${response.status}: ${response.data.message}`,
        userEmail: email,
      });
      return false;
    } catch (e) {
      console.error(e);
      sendReport(e, { name: "AuthProvider", priority: "P5" });
      const { response } = e;
      trackAccountErrors({
        action: "LOGIN",
        errorMessage: response.data.message, // To Fix, not passing the right info to <Login /> component
        userEmail: email,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const logout = useCallback(async () => {
    setIsLoading(true);
    const accessToken = getCookie(`${store.name}-accessToken`);

    try {
      if (accessToken) {
        const res = await axios.get(
          `/.netlify/functions/account-logout?countryCode=${countryCode}&accessToken=${accessToken}`
        );
        navigate("/");
        if (res.status !== 200) {
          trackAccountErrors({ action: "LOGOUT", errorMessage: res.data });
        }
      }
    } catch (e) {
      sendReport(e, { name: "AuthProvider", priority: "P5" });
    } finally {
      setAccountDetails(null);
      setShouldClearWishlist(true);
      setIsLoading(false);
    }
  }, [countryCode, sendReport, setAccountDetails, store.name, trackAccountErrors]);

  const pullAccountDetails = useCallback(async () => {
    try {
      setIsLoading(true);
      const accessToken = getCookie(`${store.name}-accessToken`);

      if (!accessToken) {
        await logout();
        return;
      }
      const res = await axios.get(`/.netlify/functions/account`, {
        params: { countryCode, accessToken },
      });
      if (res.status !== 200 || !res.data.customer) {
        // invalid token and no error from us (backend)
        await logout();
      } else {
        setAccountDetails(res.data.customer);
      }
    } catch (err) {
      sendReport(err, { name: "AuthProvider", priority: "P1" });
    } finally {
      setIsLoading(false);
    }
  }, [countryCode, store.name, logout, setAccountDetails, sendReport]);

  // If user token found:
  // - Load account data
  // - Invalidate data if token invalid (expired or other reasons)
  // - Update on store change
  useEffect(() => {
    pullAccountDetails();

    // Poll for account details every minute to ensure data is somewhat up-to-date
    const POLLING_ACCOUNT_INTERVAL = 60 * 1000; // 1 minute
    let lastPolled = Date.now();
    let intervalId;

    const poll = () => {
      const now = Date.now();
      if (now - lastPolled >= POLLING_ACCOUNT_INTERVAL) {
        lastPolled = now;
        pullAccountDetails();
      }
    };

    intervalId = setInterval(poll, POLLING_ACCOUNT_INTERVAL);

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        poll();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange); // Instant re-fetch account details when the tab is active

    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [pullAccountDetails]);

  // Expose account detail to the window object for easy access
  useEffect(() => {
    window.accountDetails = accountDetails;
  }, [accountDetails]);

  useEffect(() => {
    if (!accountDetails || !isBrowser || isLoading) {
      return;
    }
    window.exponea?.identify(
      {
        email_id: accountDetails.email,
      },
      {
        email: accountDetails.email,
      }
    );
  }, [accountDetails, isLoading]);

  const showCreateAccount = () => setAuthModalComponent(TYPE_CREATE_ACCOUNT);
  const showLogin = () => setAuthModalComponent(TYPE_LOGIN);

  const showConfirmation = ({ actionLabel, onAction, text }) => {
    setAuthModalComponent(TYPE_CONFIRM);
    setAuthModalComponentProps({ actionLabel, onAction, text });
  };

  const closeAuthModal = () => {
    setAuthModalComponent(null);
  };

  return {
    isLoading,
    error,
    accountDetails,
    setAccountDetails,
    shouldClearWishlist,
    login,
    logout,
    signup,
    authModalComponent,
    authModalComponentProps,
    closeAuthModal,
    showCreateAccount,
    showLogin,
    showConfirmation,
    pullAccountDetails,
  };
}

export function useAuth() {
  return useContext(AuthContext);
}

export default function AuthProvider({ children }) {
  const auth = useProvideAuth();

  const [loginError, setLoginError] = useState(null);
  const [loginSuccess, setLoginSuccess] = useState(null);
  const [createAccountError, setCreateAccountError] = useState(null);

  const handleLoginSubmit = (data) => {
    setLoginError(null);
    setLoginSuccess(null);

    auth.login(data.email).then((res) => {
      if (res) {
        setLoginSuccess("Check your inbox or spam folder to click on your link.");
      } else {
        setLoginError(
          <>
            Email is incorrect. If you haven&apos;t created an account yet, you can&nbsp;
            <a href="/?auth=signup" className="underline font-bold cursor-pointer">
              join here
            </a>
            .
          </>
        );
      }
    });
  };

  const handleCreateAccountSubmit = async (data) => {
    setCreateAccountError(null);
    const { email, password, firstName, lastName } = data;

    try {
      await auth.signup(email, password, firstName, lastName, `${window.location.origin}/account/`);
    } catch (e) {
      const errorMessage =
        e?.response?.data || e?.data || e?.message || "An error occurred. Please try again.";
      setCreateAccountError(`Failed to register: ${errorMessage}`);
    }
  };

  const handleClose = () => {
    setLoginError(null);
    setLoginSuccess(null);
    setCreateAccountError(null);
    auth.closeAuthModal();
  };

  let modalContent = null;
  let ctaTitle = null;
  let ctaOnClick = null;
  let ctaSuccess = null;
  let ctaError = null;
  let title = null;
  let smallPrintText = null;

  switch (auth.authModalComponent) {
    case TYPE_CREATE_ACCOUNT:
      modalContent = <CreateAccount onSubmit={handleCreateAccountSubmit} />;
      title = "Create Account";
      ctaTitle = "Sign Up";
      ctaOnClick = () => {
        const form = document.getElementById("create-account-form");
        if (form) form.requestSubmit();
      };
      ctaError = createAccountError || auth.error;
      smallPrintText = (
        <>
          By registering, I agree to Ana Luisa&#39;s&nbsp;
          <a href="/policies/terms-of-service" className="underline">
            Terms & Conditions
          </a>
          ,&nbsp;
          <a href="/policies/privacy-policy" className="underline">
            Privacy Policy
          </a>
          &nbsp; and receiving marketing emails.
          <p className="mt-[15px]">
            Already have an account?&nbsp;
            <button type="button" className="font-bold underline" onClick={auth.showLogin}>
              Login here
            </button>
          </p>
        </>
      );
      break;

    case TYPE_LOGIN:
      modalContent = <Login onSubmit={handleLoginSubmit} />;
      title = "Member Login";
      ctaTitle = "Email My Link";
      ctaOnClick = () => {
        const form = document.getElementById("login-form");
        if (form) form.requestSubmit();
      };
      ctaSuccess = loginSuccess;
      ctaError = loginError;
      smallPrintText = (
        <>
          Don&#39;t have an account?&nbsp;
          <button className="underline font-bold" type="button" onClick={auth.showCreateAccount}>
            Join here
          </button>
        </>
      );
      break;

    case TYPE_LOGIN_ERROR:
      modalContent = <LoginError />;
      title = "Login error";
      ctaTitle = "Receive a New Login Link";
      ctaOnClick = auth.showLogin;
      break;

    default:
      modalContent = null;
      break;
  }

  return (
    <AuthContext.Provider value={auth}>
      {children}
      <ALPopup
        isOpen={!!modalContent}
        onClose={handleClose}
        title={title}
        ctaTitle={ctaTitle}
        ctaOnClick={ctaOnClick}
        ctaSuccess={ctaSuccess}
        ctaError={ctaError}
        smallPrintText={smallPrintText}
        isLoading={auth.isLoading}
        showSuccessIcon={auth.authModalComponent !== TYPE_LOGIN}
        compactTitle
      >
        {modalContent}
      </ALPopup>
    </AuthContext.Provider>
  );
}

AuthProvider.propTypes = {
  children: PropTypes.node,
};
